/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './DeclaracaoSaude.css';

import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid';

import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import { BadgeTheme, Table, TabPanel } from 'components';

import { StatusType, STATUS } from 'context';

import { useAxios, useDeclaracaoSaude, useUi } from 'hooks';
import { date } from 'utils';

type RequestParamType = {
  keyword?: string | undefined;
  status: string;
  page?: number | 0;
  size: number;
  naturezaJuridica: string;
};

type ProtocolCounterType = {
  status: string;
  quantidade: number;
};

type DataType = {
  content: ResponseType[];
  pageable: {
    sort: {
      sorted: boolean;
      unsorted: boolean;
      empty: boolean;
    };
    offset: number;
    pageNumber: number;
    pageSize: number;
    unpaged: boolean;
    paged: boolean;
  };
  last: boolean;
  totalPages: number;
  totalElements: number;
  size: number;
  number: number;
  sort: {
    sorted: boolean;
    unsorted: boolean;
    empty: boolean;
  };
  first: boolean;
  numberOfElements: number;
  empty: boolean;
};

type ResponseType = {
  protocolo: string;
  nomeTitular: string;
  totalDependentes: string;
  dataPreenchimento: string;
  dataExpiracaoLink: string;
  tipoPreenchimento: string;
  idOperadora: number;
  naturezaJuridica: string;
  novaMensagem: boolean | null;
};

type RowType = {
  id: string;
  protocolo: string;
  nomeTitular: string;
  totalDependentes: string;
  dataPreenchimento: string;
  dataExpiracaoLink: string;
  diasAberto: number;
  tipoPreenchimento: string;
  idOperadora: number;
  novaMensagem: number;
  naturezaJuridica: string;
  rowBackground: 'danger' | 'default' | 'warning';
};

type Protocols = {
  [key in StatusType]?: ResponseType[];
};

type A11yProps = {
  id: string;
  'aria-controls': string;
};

const PROTOCOLO_STATUS_LABEL: { [key in StatusType]: string } = {
  'PENDENTE DE AUDITORIA': 'Pendente de Auditoria',
  'EM AUDITORIA': 'Em Auditoria',
  'PENDENTE DE RESPOSTA': 'Pendente de Resposta',
  'RESPOSTA EM ANALISE': 'Resposta em Análise',
  'PENDENTE DE ACEITE': 'Pendente de Aceite',
  CONCLUIDO: 'Concluido em Auditoria',
  'CONCLUIDO EM AGENDA': 'Concluído',
  RECUSADO: 'Recusado',
  CANCELADO: 'Cancelado',
} as const;

const AUDIT_STATUS = STATUS.filter(
  p =>
    ![
      'CANCELADO',
      'CONCLUIDO',
      'CONCLUIDO EM AGENDA',
      'PENDENTE DE RESPOSTA',
      'PENDENTE DE ACEITE',
    ].includes(p)
);
const BENEF_AUDIT_STATUS = STATUS.filter(
  p => !['CANCELADO', 'CONCLUIDO'].includes(p)
);

const a11yProps = (index: number): A11yProps => ({
  id: `simple-tab-${index}`,
  'aria-controls': `simple-tabpanel-${index}`,
});

const DeclaracaoSaude: React.FC = () => {
  const navigate = useNavigate();
  const { api } = useAxios();
  const { errorMessage, hideBackdrop, showBackdrop } = useUi();
  const { clearDs, setDs } = useDeclaracaoSaude();

  const [currentTab, setCurrentTab] = useState(() => {
    const savedTab = sessionStorage.getItem('ds_currentTab')?.trim();
    return savedTab ? parseInt(savedTab, 10) : 0;
  });

  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = sessionStorage.getItem('ds_currentPage');
    return savedPage ? parseInt(savedPage, 10) : 0;
  });

  const [currentPageSize, setCurrentPageSize] = useState(() => {
    const savedPageSize = sessionStorage.getItem('ds_currentPageSize');
    return savedPageSize ? parseInt(savedPageSize, 10) : 10;
  });

  const [currentNaturezaJuridica, setCurrentNaturezaJuridica] = useState<
    'PJ' | 'PF'
  >(() => {
    const savedNatureza = sessionStorage.getItem('ds_naturezaJuridica');
    return (savedNatureza as 'PJ' | 'PF') || 'PJ';
  });

  const [searchInput, setSearchInput] = useState<string>('');
  const [keyword, setKeyword] = useState<string>('');

  const [loading, setLoading] = useState(false);
  const [dataProtocols, setDataProtocols] = useState<DataType>({} as DataType);
  const [displayedProtocols, setDisplayedProtocols] = useState<RowType[]>([]);
  const [protocolCount, setProtocolCount] = useState<ProtocolCounterType[]>([]);
  const [viewBenefPendencies, setViewBenefPendencies] = useState<boolean>(
    () => {
      const savedBoolean = sessionStorage.getItem('ds_viewBenefPendencies');
      return savedBoolean ? JSON.parse(savedBoolean) : false;
    }
  );
  const [visibleProtocolStatus, setVisibleProtocolStatus] = useState<
    StatusType[]
  >(viewBenefPendencies ? BENEF_AUDIT_STATUS : AUDIT_STATUS);
  const [animateTabs, setAnimateTabs] = useState(false);

  useEffect(() => {
    if (viewBenefPendencies) {
      setVisibleProtocolStatus(BENEF_AUDIT_STATUS);
    } else {
      setVisibleProtocolStatus(AUDIT_STATUS);
    }
  }, [viewBenefPendencies]);

  useEffect(() => {
    clearDs();
  }, [clearDs]);

  useEffect(() => {
    if (loading) {
      showBackdrop();
    } else {
      hideBackdrop();
    }

    return () => {
      hideBackdrop();
    };
  }, [loading, hideBackdrop, showBackdrop]);

  const getRow = (f: ResponseType): RowType => {
    const {
      protocolo,
      nomeTitular,
      totalDependentes,
      dataPreenchimento,
      dataExpiracaoLink,
      tipoPreenchimento,
      idOperadora,
      novaMensagem,
      naturezaJuridica,
    } = f;

    const expirationDate = dataExpiracaoLink
      ? new Date(dataExpiracaoLink).toLocaleDateString('pt-BR')
      : '';
    const expirationDiff = new Date(dataExpiracaoLink).getTime();
    const formDate = new Date(f.dataPreenchimento).getTime();
    const actualDay = new Date().getTime();
    const difference = Math.abs(actualDay - formDate);
    const daysLeft = Math.ceil(difference / (1000 * 60 * 60 * 24));

    const notification =
      novaMensagem === true
        ? 0
        : novaMensagem === false
        ? 1
        : novaMensagem === null
        ? 2
        : 3;

    return {
      id: protocolo,
      novaMensagem: notification,
      protocolo,
      nomeTitular,
      totalDependentes,
      dataPreenchimento,
      dataExpiracaoLink:
        expirationDiff < actualDay
          ? `${expirationDate} (Expirado)`
          : `${expirationDate} (Valido)` || '',
      diasAberto: daysLeft,
      tipoPreenchimento,
      naturezaJuridica,
      idOperadora,
      rowBackground:
        daysLeft >= 24 && daysLeft <= 35
          ? 'warning'
          : daysLeft >= 35
          ? 'danger'
          : 'default',
    };
  };

  const applySearch = (): void => {
    const trimmed = searchInput.trim();
    setKeyword(trimmed);
    setCurrentPage(0);
  };

  const fetchProtocolCounter = useCallback(() => {
    const endpoint = `${process.env.REACT_APP_MS_AUDITORIA_URL}/protocolo/total`;

    api.get(endpoint).then(({ data }) => {
      setProtocolCount(data);
    });
  }, [api]);

  useEffect(() => {
    fetchProtocolCounter();
  }, [api, navigate]);

  const fetchProtocols = useCallback(
    (params?: RequestParamType) => {
      const endpoint = `${process.env.REACT_APP_MS_AUDITORIA_URL}/protocolo/v4/protocolo-status`;

      setLoading(true);

      api
        .get<DataType>(endpoint, { params })
        .then(({ data }) => {
          setDataProtocols(data);
          if (data.content) {
            const formattedProtocols = data.content.map(getRow);
            const filteredProtocols = formattedProtocols.filter(
              p => p.naturezaJuridica === currentNaturezaJuridica
            );
            setDisplayedProtocols(filteredProtocols);
          }
        })
        .catch(err => {
          navigate('/error');
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [api, navigate, currentNaturezaJuridica]
  );

  useEffect(() => {
    setDisplayedProtocols([]);
    const status = visibleProtocolStatus[currentTab];
    fetchProtocols({
      status,
      page: currentPage,
      size: currentPageSize,
      naturezaJuridica: currentNaturezaJuridica,
      keyword,
    });
  }, [
    api,
    navigate,
    currentTab,
    currentNaturezaJuridica,
    currentPage,
    currentPageSize,
    keyword,
  ]);

  const columns: GridColDef[] = [
    {
      field: 'novaMensagem',
      type: 'action',
      renderCell: (params: GridRenderCellParams) =>
        params.value === 0 ? (
          <Tooltip title="Novas mensagens">
            <ChatBubbleIcon fontSize="small" color="primary" />
          </Tooltip>
        ) : params.value === 1 ? (
          <Tooltip title="Sem novas mensagens">
            <ChatBubbleOutlineIcon fontSize="small" color="disabled" />
          </Tooltip>
        ) : params.value === 2 ? (
          <Tooltip title="Chat não utilizado">
            <ChatBubbleOutlineIcon
              fontSize="small"
              sx={{ color: 'transparent' }}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Chat não utilizado">
            <ChatBubbleOutlineIcon
              fontSize="small"
              sx={{ color: 'transparent' }}
            />
          </Tooltip>
        ),
      headerName: '',
      align: 'center',
      headerAlign: 'center',
      width: 60,
      disableColumnMenu: true,
    },
    { field: 'protocolo', headerName: 'Protocolo', flex: 1 },
    { field: 'nomeTitular', headerName: 'Titular', flex: 1 },
    {
      field: 'totalDependentes',
      headerName: 'Total de Dependentes',
      flex: 1,
      align: 'center',
    },
    {
      field: 'dataPreenchimento',
      headerName: 'Data de Preenchimento',
      flex: 1,
      valueFormatter: (params: { value: string }) =>
        new Date(params.value as string).toLocaleDateString('pt-BR'),
      align: 'center',
    },
    {
      field: 'diasAberto',
      headerName: 'Dias em Aberto',
      width: 130,
      align: 'center',
    },
    {
      field: 'dataExpiracaoLink',
      headerName: 'Data de Expiração',
      flex: 1,
      align: 'center',
      hide:
        currentTab === 0 ||
        currentTab === 1 ||
        currentTab === 3 ||
        currentTab === 5,
    },
    {
      field: 'tipoPreenchimento',
      headerName: 'Tipo de Preenchimento',
      flex: 1,
    },
  ];

  const getColumns = (status: StatusType): GridColDef[] => {
    const baseColumns = [...columns];
    if (status === 'CONCLUIDO EM AGENDA') {
      return baseColumns.filter(col => col.field !== 'diasAberto');
    }
    return baseColumns;
  };

  const onRowClick = (gridRow: GridRowParams, status: StatusType): void => {
    const f = displayedProtocols.find(
      ({ protocolo }) => protocolo === gridRow.id
    );

    if (!f) {
      errorMessage('Não localizamos os dados desse formulário.');
      return;
    }

    const fetchFormulariosDetails = (): void => {
      const url = `${process.env.REACT_APP_MS_AGENDA_URL}/formulario/operadora/${f.idOperadora}/protocolo/${gridRow.id}`;

      setLoading(true);
      api
        .get(url)
        .then(({ data }) => {
          setDs({
            protocolo: f.protocolo,
            formulario: JSON.parse(data.formulario),
            tipoPreenchimento: gridRow.row.tipoPreenchimento,
            status,
            operadora: f.idOperadora,
            naturezaJuridica: f.naturezaJuridica,
          });
          navigate(`/declaracao-de-saude/${gridRow.id}`);
        })
        .catch(() => {
          navigate('/error');
        })
        .finally(() => {
          setLoading(false);
        });
    };
    fetchFormulariosDetails();
  };

  const handleChangeTab = (tabIndex: number): void => {
    setCurrentPage(0);
    setCurrentTab(tabIndex);
    sessionStorage.setItem('ds_currentPage', '0');
    sessionStorage.setItem('ds_currentTab', tabIndex.toString());
  };

  const renderLabelWithBadge = (status: StatusType): React.ReactElement => {
    const quantidade = protocolCount.find(p => p.status === status)?.quantidade;
    return (
      <BadgeTheme color="primary" badgeContent={quantidade || 0}>
        {PROTOCOLO_STATUS_LABEL[status]}
      </BadgeTheme>
    );
  };

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={currentTab}
          onChange={(e, i) => handleChangeTab(i)}
          centered
        >
          {visibleProtocolStatus.map((status, index) => (
            <Tab
              label={renderLabelWithBadge(status)}
              {...a11yProps(index)}
              wrapped
              className={animateTabs ? 'blink-animation' : ''}
            />
          ))}
        </Tabs>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
        <TextField
          placeholder="Buscar por palavra-chave"
          variant="outlined"
          size="small"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              applySearch();
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {searchInput.trim().length > 0 && (
                  <IconButton
                    aria-label="Limpar pesquisa"
                    onClick={() => {
                      setSearchInput('');
                      setKeyword('');
                      setCurrentPage(0);
                      sessionStorage.setItem('ds_currentPage', '0');
                    }}
                    edge="end"
                    size="small"
                  >
                    <CloseIcon fontSize="small" />{' '}
                  </IconButton>
                )}
                <IconButton
                  aria-label="Pesquisar"
                  onClick={applySearch}
                  edge="end"
                  size="small"
                >
                  <SearchIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1 }}
        />{' '}
        <Button
          variant="text"
          startIcon={
            <VisibilityIcon
              sx={{ color: viewBenefPendencies ? '##00995D' : 'lightgray' }}
            />
          }
          onClick={() => {
            setViewBenefPendencies(prev => {
              sessionStorage.setItem(
                'ds_viewBenefPendencies',
                JSON.stringify(!prev)
              );
              return !prev;
            });
            setAnimateTabs(true);
            setTimeout(() => setAnimateTabs(false), 1200);
            handleChangeTab(0);
          }}
          sx={{
            display: 'flex',
            alignSelf: 'start',
            padding: 0,
            margin: 0,
            textTransform: 'none',
            color: viewBenefPendencies ? '#00995D' : 'lightgray',
          }}
        >
          Todos
        </Button>
      </Box>
      <ToggleButtonGroup
        color="secondary"
        value={currentNaturezaJuridica}
        exclusive
        onChange={(_, newNaturezaJuridica) => {
          if (newNaturezaJuridica !== null) {
            setCurrentPage(0);
            setCurrentNaturezaJuridica(newNaturezaJuridica);
            sessionStorage.setItem('ds_naturezaJuridica', newNaturezaJuridica);
            sessionStorage.setItem('ds_currentPage', '0');
          }
        }}
        size="small"
        fullWidth
        sx={{ mt: 0.5 }}
      >
        <ToggleButton sx={{ fontSize: 12 }} value="PJ">
          PESSOA JURÍDICA
        </ToggleButton>
        <ToggleButton sx={{ fontSize: 12 }} value="PF">
          PESSOA FÍSICA
        </ToggleButton>
      </ToggleButtonGroup>

      {visibleProtocolStatus.map((status, index) => (
        <TabPanel
          value={currentTab}
          index={index}
          key={`${status}_${date.getCurrentMillis()}`}
        >
          <Box sx={{ minHeight: 165, width: '100%' }}>
            <Table
              initialState={{
                pagination: { pageSize: 10 },
                sorting: {
                  sortModel: [{ field: 'dataPreenchimento', sort: 'asc' }],
                },
              }}
              loading={loading}
              rows={displayedProtocols}
              rowsPerPageOptions={[5, 10]}
              page={currentPage}
              pageSize={currentPageSize}
              pagination
              paginationMode="server"
              rowCount={dataProtocols.totalElements || 0}
              columns={getColumns(status)}
              autoHeight
              onRowClick={gridRow => onRowClick(gridRow, status)}
              onPageChange={newPage => {
                setCurrentPage(newPage);
                sessionStorage.setItem('ds_currentPage', newPage.toString());
              }}
              onPageSizeChange={newPageSize => {
                setCurrentPageSize(newPageSize);
                sessionStorage.setItem(
                  'ds_currentPageSize',
                  newPageSize.toString()
                );
                const convertedPage =
                  newPageSize === 10
                    ? Math.floor(currentPage / 2)
                    : currentPage * 2;
                setCurrentPage(convertedPage);
                sessionStorage.setItem(
                  'ds_currentPage',
                  convertedPage.toString()
                );
              }}
              sx={{
                '& .MuiDataGrid-row:hover': { cursor: 'pointer' },
                '& .row-background-danger': {
                  backgroundColor: '#ffd8d8',
                },
                '& .row-background-warning': {
                  backgroundColor: '#fff6d9',
                },
              }}
              getRowClassName={params => {
                if (status === 'CONCLUIDO EM AGENDA') {
                  return 'row-background';
                }
                return `row-background-${params.row.rowBackground}`;
              }}
            />
          </Box>
        </TabPanel>
      ))}
    </Box>
  );
};

export default DeclaracaoSaude;
